/**
 * Globální themed confirm/prompt — náhrada window.confirm / window.prompt.
 *
 * Host (ThemedDialogsHost) registruje implementaci; call-site jen awaituje.
 * Bez hosta fallback na nativní dialog (dev / edge).
 */
import type { ConfirmAskOptions } from '../hooks/useConfirmDialog';
import type { PromptAskOptions } from '../hooks/usePromptDialog';

type ConfirmFn = (opts: ConfirmAskOptions) => Promise<boolean>;
type PromptFn = (opts: PromptAskOptions) => Promise<string | null>;

let confirmImpl: ConfirmFn | null = null;
let promptImpl: PromptFn | null = null;

export function registerThemedConfirm(fn: ConfirmFn | null): void {
  confirmImpl = fn;
}

export function registerThemedPrompt(fn: PromptFn | null): void {
  promptImpl = fn;
}

/** Themed confirm. `message` = description; title default „Potvrdit". */
export async function themedConfirm(
  messageOrOpts: string | ConfirmAskOptions,
  danger = true,
): Promise<boolean> {
  const opts: ConfirmAskOptions = typeof messageOrOpts === 'string'
    ? { title: messageOrOpts, description: undefined, danger }
    : { danger, ...messageOrOpts };

  if (confirmImpl) return confirmImpl(opts);

  // Fallback (SSR / před mountem hosta)
  if (typeof window === 'undefined') return false;
  const text = [opts.title, opts.description].filter(Boolean).join('\n\n');
  return window.confirm(text);
}

/**
 * Fire-and-forget: po potvrzení spustí `action`.
 * Vhodné pro onClick bez async handleru.
 */
export function confirmThen(
  messageOrOpts: string | ConfirmAskOptions,
  action: () => void,
  danger = true,
): void {
  void themedConfirm(messageOrOpts, danger).then((ok) => {
    if (ok) action();
  });
}

/** Themed prompt. Vrací string nebo null (zrušeno). */
export async function themedPrompt(
  messageOrOpts: string | PromptAskOptions,
  defaultValue = '',
): Promise<string | null> {
  const opts: PromptAskOptions = typeof messageOrOpts === 'string'
    ? { title: messageOrOpts, defaultValue }
    : { defaultValue, ...messageOrOpts };

  if (promptImpl) return promptImpl(opts);

  if (typeof window === 'undefined') return null;
  return window.prompt(opts.title, opts.defaultValue ?? '');
}
